import { useId, useState, type SubmitEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { api, errorMessage } from '../api';
import { AuthError, AuthLink, AuthShell } from './AuthShell';

interface MfaPageProps {
  onSuccess: () => void;
  onCancel: () => void;
}

/** Second step for accounts that turned 2FA on: the 6-digit code from the authenticator app. */
export function MfaPage({ onSuccess, onCancel }: MfaPageProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const id = useId();

  async function submit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await api.verifyTotp(code);
      onSuccess();
    } catch (caught) {
      setError(errorMessage(caught));
      setCode('');
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthShell
      title="Nhập mã xác thực"
      description="Mở ứng dụng xác thực trên điện thoại và nhập mã 6 số đang hiện."
    >
      <form className="grid gap-4" onSubmit={submit}>
        <div className="grid gap-2">
          <Label htmlFor={`${id}-code`}>Mã 6 số</Label>
          <Input
            id={`${id}-code`}
            className="h-14 text-center font-mono text-2xl tracking-[0.5em]"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="\d{6}"
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            autoFocus
          />
        </div>
        {error && <AuthError>{error}</AuthError>}
        <Button type="submit" size="lg" className="h-11" disabled={pending || code.length !== 6}>
          {pending ? 'Đang xác thực…' : 'Xác nhận'}
        </Button>
        <AuthLink onClick={onCancel}>Quay lại đăng nhập</AuthLink>
      </form>
    </AuthShell>
  );
}
